import { z } from 'zod';

import { LosId } from '../BrandedIds';
import {
  AutopayEmailData,
  BorrowerEmailData,
  BorrowerEmailHtmlData,
  BorrowerInsuranceExpirationEmailData,
  DefermentCancelledEmailData,
  DelinquentInterestEmailData,
  EscrowAnalysisAvailableEmailData,
  FinalPaymentDueEmailData,
  Form1098AvailableEmailData,
  GoodbyeBeforeWelcomeEmailData,
  GoodbyeEmailData,
  LoanIsMaturingEmailData,
  PaidInFullStatementEmailData,
  PartialPayoffConfirmationEmailData,
  PaymentDueAutopayEmailData,
  PaymentDueEmailData,
  PaymentFailedAutopayEmailData,
  PaymentFailedEmailData,
  PaymentMadeEmailData,
  PaymentReminderAutopayEmailData,
  PaymentReminderEmailData,
  PrincipalModificationAppliedEmailData,
  RescindGoodbyeEmailData,
  WelcomeEmailData,
  WelcomePreActiveEmailData,
} from './BorrowerEmailContext';
import { ExternalPaymentDueEmailData } from './constants/externalPaymentDue';
import { ExternalPayoffQuoteEmailData } from './constants/externalPayoffQuote';
import { MortgageeLetterEmail, MortgageeLetterEmailData } from './constants/mortgageeLetter';

/**
 * Steps when adding a new email mjml template:
 * 1) add a const literal for the new template name (borrower/lender group)
 * 2) add this const literal to EmailTemplateType
 * 3) also add to the appropriate type groups
 *    - usually BaseEmailTemplateType if email uses SendBaseEmailEvent/SendBaseEmailSideEffectContext
 *    (Send...EmailEvent and Send...EmailSideEffectContext should match)
 * 4) update EmailMapping interface - match this literal with its EmailData
 *
 * 5) getEmailContext.ts: add literal case to match with appropriate context in EmailArgsMapping
 *    - then update: sideEffects/sendEmail.ts for borrower email side effect or handleSendEmail.ts for borrower email event
 * 6) EmailHtmlService.ts: add subject and header message for literal case
 * 7) CompanyLoanResolvers.ts: add emailName case
 *
 * 8) shared-node/scripts/sendEmail.ts: update development script to test
 */

/// step 1
/* Borrower Emails */
export const GoodbyeEmail = z.literal('goodbye');
export const GoodbyeBeforeWelcome = z.literal('goodbyeBeforeWelcome');
const LatePaymentEmail = z.literal('latePayment');
export const GracePastDueEmail = z.literal('gracePastDue');
const NextStatementEmail = z.literal('nextStatementAvailable');
const NextStatementInterestReserveEmail = z.literal('nextStatementAvailableInterestReserve');
const NextStatementAutopayEmail = z.literal('nextStatementAvailableAutopay');
const BorrowerRevisedStatement = z.literal('borrowerRevisedStatement');
export const PaymentCancelledEmail = z.literal('paymentCancelled');
export const PaymentFailedEmail = z.literal('paymentFailed');
export const PaymentFailedAutopayEmail = z.literal('paymentFailedAutopay');
const PaymentSubmittedEmail = z.literal('paymentSubmitted');
const PaymentPostedEmail = z.literal('paymentPosted');
const PaymentPostedInterestReserveEmail = z.literal('paymentPostedInterestReserve');
const PaymentReminderEmail = z.literal('paymentReminder');
const PaymentPastDueReminderEmail = z.literal('paymentPastDueReminder');
const PaymentReminderAutopayEmail = z.literal('paymentReminderAutopay');
export const RescindGoodbyeEmail = z.literal('rescindGoodbye');
export const WelcomeEmail = z.literal('welcome');
export const WelcomePreActiveEmail = z.literal('welcomePreActive');
export const WelcomeInterestReserveEmail = z.literal('welcomeInterestReserve');
export const WelcomeAutoPayEmail = z.literal('welcomeAutoPay');
const AutopayEnabledEmail = z.literal('autopayEnabled');
const AutopayDisabledEmail = z.literal('autopayDisabled');
const BorrowerSuspendedEmail = z.literal('borrowerSuspended');
const BorrowerAccountCreatedEmail = z.literal('borrowerAccountCreated');
const BorrowerAccountVerifiedEmail = z.literal('borrowerAccountVerified');
const BorrowerAccountRemovedEmail = z.literal('borrowerAccountRemoved');
const BorrowerUserVerifiedEmail = z.literal('borrowerUserVerified');
const BorrowerDocumentationRequiredEmail = z.literal('borrowerDocumentationRequired');
const BorrowerDocumentationAddedEmail = z.literal('borrowerDocumentationAdded');
const BorrowerDocumentationRejectedEmail = z.literal('borrowerDocumentationRejected');
const BorrowerDocumentationApprovedEmail = z.literal('borrowerDocumentationApproved');
const BorrowerMicroDepositPostedEmail = z.literal('borrowerMicroDepositPosted');
const BorrowerErrorEmail = z.literal('borrowerError');
const PayoffQuote = z.literal('payoffQuote');
const NewDocumentAvailable = z.literal('newDocumentAvailable');
export const PaidInFullStatementEmail = z.literal('paidInFullStatement');
export const PartialPayoffConfirmationEmail = z.literal('partialPayoffConfirmation');
export const PrincipalModificationAppliedEmail = z.literal('principalModificationApplied');
export const PrincipalModificationCancelledEmail = z.literal('principalModificationCancelled');
const Form1098AvailableEmail = z.literal('form1098Available');
const EscrowAnalysisAvailableEmail = z.literal('escrowAnalysisAvailable');
const BorrowerDelinquentInterestAppliedEmail = z.literal('borrowerDelinquentInterestApplied');
export const FinalPaymentEmail = z.literal('finalPayment');
const PortalInstructionsEmail = z.literal('portalInstructions');
const DefermentCancelledEmail = z.literal('defermentCancelled');
const LoanIsMaturing = z.literal('loanIsMaturing');
export const BorrowerInsuranceExpiringSoonEmail = z.literal('borrowerInsuranceExpiringSoon');
export type BorrowerInsuranceExpiringSoonEmail = z.infer<typeof BorrowerInsuranceExpiringSoonEmail>;
export const BorrowerInsuranceExpiredEmail = z.literal('borrowerInsuranceExpired');
export type BorrowerInsuranceExpiredEmail = z.infer<typeof BorrowerInsuranceExpiredEmail>;

/* Lender Emails */
const LenderWelcomeEmail = z.literal('lenderWelcome');
const LenderWelcomeSsoEmail = z.literal('lenderWelcomeSso');
const LenderAccountSuspendedEmail = z.literal('lenderAccountSuspended');
const LenderDocumentationRequiredEmail = z.literal('lenderDocumentationRequired');
const LenderDocumentationAddedEmail = z.literal('lenderDocumentationAdded');
const LenderDocumentationApprovedEmail = z.literal('lenderDocumentationApproved');
const LenderDocumentationRejectedEmail = z.literal('lenderDocumentationRejected');
const LenderPaymentCancelledEmail = z.literal('lenderPaymentCancelled');
export const LenderPaymentFailedEmail = z.literal('lenderPaymentFailed');
const LenderAccountVerifiedEmail = z.literal('lenderAccountVerified');
const LenderAccountRemovedEmail = z.literal('lenderAccountRemoved');
const LenderUserVerifiedEmail = z.literal('lenderUserVerified');
const LenderPaymentPostedEmail = z.literal('lenderPaymentPosted');
const LenderPaymentSubmittedEmail = z.literal('lenderPaymentSubmitted');
const LenderDownloadReportEmail = z.literal('lenderDownloadReport');
const LenderSidePaymentFailedEmail = z.literal('lenderSidePaymentFailed');
const LenderMicroDepositPostedEmail = z.literal('lenderMicroDepositPosted');
const LenderSweepCompleteEmail = z.literal('lenderSweepComplete');
const LenderSweepFailedEmail = z.literal('lenderSweepFailed');
const LenderGeneratePayoffReminderEmail = z.literal('lenderGeneratePayoffReminder');
const LenderForm1098sDraftedEmail = z.literal('lenderForm1098sDrafted');

/* External Emails */
export const ExternalPayoffQuoteEmail = z.literal('externalPayoffQuote');
export const ExternalPaymentDueEmail = z.literal('externalPaymentDue');

/// step 2
/* Types */
export const EmailTemplateType = z.union([
  LatePaymentEmail,
  GracePastDueEmail,
  NextStatementEmail,
  NextStatementInterestReserveEmail,
  NextStatementAutopayEmail,
  FinalPaymentEmail,
  BorrowerRevisedStatement,
  PaymentReminderEmail,
  PaymentPastDueReminderEmail,
  PaymentReminderAutopayEmail,
  WelcomeEmail,
  WelcomePreActiveEmail,
  WelcomeInterestReserveEmail,
  WelcomeAutoPayEmail,
  AutopayEnabledEmail,
  AutopayDisabledEmail,
  BorrowerSuspendedEmail,
  BorrowerAccountCreatedEmail,
  BorrowerAccountVerifiedEmail,
  BorrowerAccountRemovedEmail,
  BorrowerUserVerifiedEmail,
  BorrowerDocumentationRequiredEmail,
  BorrowerDocumentationAddedEmail,
  BorrowerDocumentationRejectedEmail,
  BorrowerDocumentationApprovedEmail,
  BorrowerMicroDepositPostedEmail,
  BorrowerInsuranceExpiringSoonEmail,
  BorrowerInsuranceExpiredEmail,
  BorrowerErrorEmail,
  BorrowerDelinquentInterestAppliedEmail,
  PaidInFullStatementEmail,
  PartialPayoffConfirmationEmail,
  NewDocumentAvailable,
  Form1098AvailableEmail,
  EscrowAnalysisAvailableEmail,
  DefermentCancelledEmail,
  PortalInstructionsEmail,
  PrincipalModificationAppliedEmail,
  PrincipalModificationCancelledEmail,
  LoanIsMaturing,

  // Lender emails
  LenderAccountSuspendedEmail,
  LenderDocumentationRequiredEmail,
  LenderDocumentationAddedEmail,
  LenderDocumentationApprovedEmail,
  LenderDocumentationRejectedEmail,
  LenderAccountVerifiedEmail,
  LenderAccountRemovedEmail,
  LenderUserVerifiedEmail,
  LenderMicroDepositPostedEmail,
  LenderSweepCompleteEmail,
  LenderSweepFailedEmail,
  LenderGeneratePayoffReminderEmail,
  LenderForm1098sDraftedEmail,
  LenderPaymentCancelledEmail,
  LenderPaymentFailedEmail,
  LenderPaymentPostedEmail,
  LenderPaymentSubmittedEmail,
  LenderSidePaymentFailedEmail,
  LenderDownloadReportEmail, // report context
  LenderWelcomeEmail, // lender welcome context
  LenderWelcomeSsoEmail,

  PayoffQuote,

  // payment context
  PaymentCancelledEmail,
  PaymentFailedEmail,
  PaymentFailedAutopayEmail,
  PaymentPostedEmail,
  PaymentPostedInterestReserveEmail,
  PaymentSubmittedEmail,

  // transfer context
  GoodbyeEmail,
  GoodbyeBeforeWelcome,
  RescindGoodbyeEmail,

  // External Emails
  MortgageeLetterEmail,
  ExternalPayoffQuoteEmail,
  ExternalPaymentDueEmail,
]);
export type EmailTemplateType = z.infer<typeof EmailTemplateType>;

/// step 3
export const BaseEmailTemplateType = z.union([
  BorrowerErrorEmail,
  LatePaymentEmail,
  GracePastDueEmail,
  NextStatementEmail,
  NextStatementInterestReserveEmail,
  NextStatementAutopayEmail,
  FinalPaymentEmail,
  BorrowerRevisedStatement,
  PaymentReminderEmail,
  PaymentPastDueReminderEmail,
  PaymentReminderAutopayEmail,
  WelcomeEmail,
  WelcomePreActiveEmail,
  WelcomeInterestReserveEmail,
  WelcomeAutoPayEmail,
  NewDocumentAvailable,
  DefermentCancelledEmail,
  BorrowerDelinquentInterestAppliedEmail,
  PortalInstructionsEmail,
  LoanIsMaturing,
  PayoffQuote,
]);

// One-off templates that use their own mjml file or are sent to external parties
export const ExternalEmailTemplateType = z.union([
  MortgageeLetterEmail,
  ExternalPayoffQuoteEmail,
  ExternalPaymentDueEmail,
]);
export type ExternalEmailTemplateType = z.infer<typeof ExternalEmailTemplateType>;

export const NextStatementEmailTemplateType = z.union([
  NextStatementEmail,
  NextStatementInterestReserveEmail,
  NextStatementAutopayEmail,
]);
export type NextStatementEmailTemplateType = z.infer<typeof NextStatementEmailTemplateType>;

export const GeneratePayoffReminderEmailType = LenderGeneratePayoffReminderEmail;
export type GeneratePayoffReminderEmailType = z.infer<typeof GeneratePayoffReminderEmailType>;

// payment emails need a specific paymentId
export const PaymentEmailTemplateType = z.union([
  PaymentCancelledEmail,
  PaymentFailedEmail,
  PaymentPostedEmail,
  PaymentSubmittedEmail,
  PaymentPostedInterestReserveEmail,
]);
export type PaymentEmailTemplateType = z.infer<typeof PaymentEmailTemplateType>;
export const PaymentLenderEmailTemplateType = z.union([
  LenderPaymentCancelledEmail,
  LenderPaymentFailedEmail,
  LenderPaymentPostedEmail,
  LenderPaymentSubmittedEmail,
  LenderSidePaymentFailedEmail,
  LenderSweepCompleteEmail,
  LenderSweepFailedEmail,
]);
export type PaymentLenderEmailTemplateType = z.infer<typeof PaymentLenderEmailTemplateType>;

export const UpdateAutopayEmailTemplateType = z.union([AutopayEnabledEmail, AutopayDisabledEmail]);
export type UpdateAutopayEmailTemplateType = z.infer<typeof UpdateAutopayEmailTemplateType>;

export const DocumentEmailTemplateType = z.union([Form1098AvailableEmail, EscrowAnalysisAvailableEmail]);
export type DocumentEmailTemplateType = z.infer<typeof DocumentEmailTemplateType>;

// keeping track of lender email type choices for build.ts that use baseTemplateLender
export const LenderEmailTemplateType = z.union([
  LenderWelcomeEmail,
  LenderWelcomeSsoEmail,
  LenderAccountSuspendedEmail,
  LenderDocumentationRequiredEmail,
  LenderDocumentationAddedEmail,
  LenderDocumentationApprovedEmail,
  LenderDocumentationRejectedEmail,
  LenderAccountVerifiedEmail,
  LenderAccountRemovedEmail,
  LenderUserVerifiedEmail,
  LenderDownloadReportEmail,
  LenderMicroDepositPostedEmail,
  GeneratePayoffReminderEmailType,
  LenderForm1098sDraftedEmail,
  ...PaymentLenderEmailTemplateType.options,
]);
export type LenderEmailTemplateType = z.infer<typeof LenderEmailTemplateType>;

// track all of the lender emails that are toggable in the notifications setting page
export const LenderToggableEmails = z.union([
  LenderPaymentSubmittedEmail,
  LenderPaymentPostedEmail,
  LenderPaymentFailedEmail,
  LenderPaymentCancelledEmail,
  LenderSweepCompleteEmail,
  LenderSweepFailedEmail,
  LenderAccountVerifiedEmail,
  LenderAccountRemovedEmail,
]);
export type LenderToggableEmails = z.infer<typeof LenderToggableEmails>;

// BorrowerEmailTemplateType is the inverse of LenderEmailTemplateTypes
// If we end up introducing a new role outside of Borrower and Lender that gets its own set of emails,
// Then we need to define BorrowerEmailTemplateType explicitly like we've done with other template types.
export const NonBorrowerTemplateType = z.union([LenderEmailTemplateType, ExternalEmailTemplateType]);
export type NonBorrowerTemplateType = z.infer<typeof NonBorrowerTemplateType>;
export type BorrowerEmailTemplateType = Exclude<EmailTemplateType, NonBorrowerTemplateType>;

// keeping track of dwolla specific emails that dont have loan context and are directly sent without event/sideEffect
export const DwollaUserEmailTemplateType = z.union([
  BorrowerSuspendedEmail,
  BorrowerAccountCreatedEmail,
  BorrowerAccountVerifiedEmail,
  BorrowerAccountRemovedEmail,
  BorrowerUserVerifiedEmail,
  BorrowerDocumentationRequiredEmail,
  BorrowerDocumentationAddedEmail,
  BorrowerDocumentationRejectedEmail,
  BorrowerDocumentationApprovedEmail,
  BorrowerMicroDepositPostedEmail,
]);
export type DwollaUserEmailTemplateType = z.infer<typeof DwollaUserEmailTemplateType>;
export const DwollaCompanyEmailTemplateType = z.union([
  LenderAccountSuspendedEmail,
  LenderAccountVerifiedEmail,
  LenderAccountRemovedEmail,
  LenderDocumentationAddedEmail,
  LenderDocumentationRejectedEmail,
  LenderDocumentationRequiredEmail,
  LenderDocumentationApprovedEmail,
  LenderUserVerifiedEmail,
  LenderMicroDepositPostedEmail,
]);
export type DwollaCompanyEmailTemplateType = z.infer<typeof DwollaCompanyEmailTemplateType>;

// These are the emails that a company is allowed to view
export const ViewableEmailTemplateType = z.union([
  WelcomePreActiveEmail,
  WelcomeEmail,
  WelcomeInterestReserveEmail,
  WelcomeAutoPayEmail,
  NextStatementEmail,
  NextStatementAutopayEmail,
  FinalPaymentEmail,
  BorrowerRevisedStatement,
  PaymentReminderEmail,
  PaymentReminderAutopayEmail,
  GracePastDueEmail,
  PaymentSubmittedEmail,
  PaymentPostedEmail,
  PaymentFailedEmail,
  PaymentFailedAutopayEmail,
  PaymentCancelledEmail,
  LatePaymentEmail,
  GoodbyeEmail,
  GoodbyeBeforeWelcome,
  RescindGoodbyeEmail,
  BorrowerDelinquentInterestAppliedEmail,
  LoanIsMaturing,
  PayoffQuote,
  ExternalPayoffQuoteEmail,
  PaidInFullStatementEmail,
  AutopayEnabledEmail,
  AutopayDisabledEmail,
]);
export type ViewableEmailTemplateType = z.infer<typeof ViewableEmailTemplateType>;

// WARNING: DO NOT REMOVE FIELDS from the customizable email types and be careful what you edit.
// Saved versions of the email template may still reference the data you're editing.
// Instead of removing or renaming fields, just add new ones.
export const CustomizableEmailTemplateType = z.union([
  WelcomeEmail,
  WelcomePreActiveEmail,
  WelcomeInterestReserveEmail,
  WelcomeAutoPayEmail,
  GoodbyeEmail,
  GoodbyeBeforeWelcome,
  RescindGoodbyeEmail,
  NextStatementEmail,
  NextStatementAutopayEmail,
  PaymentReminderEmail,
  PaymentReminderAutopayEmail,
  PaymentSubmittedEmail,
  PaymentPostedEmail,
  LatePaymentEmail,
  GracePastDueEmail,
  PaymentFailedEmail,
  PaymentFailedAutopayEmail,
  LoanIsMaturing,
  PayoffQuote,
  ExternalPayoffQuoteEmail,
  PaidInFullStatementEmail,
  AutopayEnabledEmail,
  AutopayDisabledEmail,
]);
export type CustomizableEmailTemplateType = z.infer<typeof CustomizableEmailTemplateType>;

export interface LenderSideEffectEmailMapping {
  lenderPaymentCancelled: LenderPaymentEmailData;
  lenderPaymentFailed: LenderPaymentFailedEmailData;
  lenderPaymentPosted: LenderPaymentEmailData;
  lenderPaymentSubmitted: LenderPaymentEmailData;
  lenderSidePaymentFailed: LenderPaymentEmailData;
  lenderSweepComplete: LenderSweepEmailData;
  lenderSweepFailed: LenderSweepEmailData;
  lenderGeneratePayoffReminder: LenderPayoffReminderEmailData;
}
/// step 4
export interface EmailMapping extends LenderSideEffectEmailMapping {
  portalInstructions: BorrowerEmailData;
  goodbyeBeforeWelcome: GoodbyeBeforeWelcomeEmailData;
  goodbye: GoodbyeEmailData;
  latePayment: PaymentDueEmailData;
  gracePastDue: PaymentDueEmailData;
  nextStatementAvailable: PaymentDueEmailData;
  nextStatementAvailableInterestReserve: PaymentDueEmailData;
  nextStatementAvailableAutopay: PaymentDueAutopayEmailData;
  finalPayment: FinalPaymentDueEmailData;
  borrowerRevisedStatement: PaymentDueEmailData;
  paymentCancelled: PaymentMadeEmailData;
  paymentFailed: PaymentFailedEmailData;
  paymentFailedAutopay: PaymentFailedAutopayEmailData;
  paymentSubmitted: PaymentMadeEmailData;
  paymentPosted: PaymentMadeEmailData;
  paymentPostedInterestReserve: PaymentMadeEmailData;
  paymentReminder: PaymentReminderEmailData;
  paymentPastDueReminder: PaymentReminderEmailData;
  paymentReminderAutopay: PaymentReminderAutopayEmailData;
  rescindGoodbye: RescindGoodbyeEmailData;
  welcome: WelcomeEmailData;
  welcomePreActive: WelcomePreActiveEmailData;
  welcomeInterestReserve: WelcomeEmailData;
  welcomeAutoPay: WelcomeEmailData;
  autopayEnabled: AutopayEmailData;
  autopayDisabled: AutopayEmailData;
  borrowerAccountCreated: BorrowerEmailData;
  borrowerAccountVerified: BorrowerDwollaAccountData;
  borrowerAccountRemoved: BorrowerDwollaAccountData;
  borrowerUserVerified: BorrowerEmailData;
  borrowerSuspended: BorrowerEmailData;
  borrowerDocumentationRequired: BorrowerEmailData;
  borrowerDocumentationAdded: BorrowerEmailData;
  borrowerDocumentationRejected: BorrowerEmailData;
  borrowerDocumentationApproved: BorrowerEmailData;
  borrowerMicroDepositPosted: BorrowerEmailData;
  borrowerError: BorrowerEmailData;
  borrowerDelinquentInterestApplied: DelinquentInterestEmailData;
  newDocumentAvailable: BorrowerEmailData;
  payoffQuote: BorrowerEmailData;
  paidInFullStatement: PaidInFullStatementEmailData;
  partialPayoffConfirmation: PartialPayoffConfirmationEmailData;
  form1098Available: Form1098AvailableEmailData;
  escrowAnalysisAvailable: EscrowAnalysisAvailableEmailData;
  defermentCancelled: DefermentCancelledEmailData;
  principalModificationApplied: PrincipalModificationAppliedEmailData;
  principalModificationCancelled: PrincipalModificationAppliedEmailData;
  loanIsMaturing: LoanIsMaturingEmailData;
  borrowerInsuranceExpiringSoon: BorrowerInsuranceExpirationEmailData;
  borrowerInsuranceExpired: BorrowerInsuranceExpirationEmailData;
  /* Lender Emails */
  lenderWelcome: LenderWelcomeEmailData;
  lenderWelcomeSso: BaseLenderEmailData;
  lenderAccountSuspended: NoEmailData;
  lenderDocumentationRequired: BaseLenderPaymentEmailData;
  lenderDocumentationAdded: BaseLenderPaymentEmailData;
  lenderDocumentationApproved: BaseLenderPaymentEmailData;
  lenderDocumentationRejected: BaseLenderPaymentEmailData;
  lenderAccountVerified: DwollaAccountData;
  lenderAccountRemoved: DwollaAccountData;
  lenderUserVerified: BaseLenderEmailData;
  lenderDownloadReport: LenderDownloadEmailData;
  lenderMicroDepositPosted: BaseLenderEmailData;
  lenderForm1098sDrafted: LenderQcDocsDraftedArgs;

  /* External emails */
  mortgageeLetter: MortgageeLetterEmailData;
  externalPayoffQuote: ExternalPayoffQuoteEmailData;
  externalPaymentDue: ExternalPaymentDueEmailData;
}

// keeping track of email data interfaces by sideEffects group (BorrowerEmailHtmlData for sendEmail and LenderEmailHtmlData for sendLenderEmail)
export type LenderEmailHtmlData =
  | LenderPaymentEmailData
  | LenderPaymentFailedEmailData
  | LenderDownloadEmailData
  | LenderWelcomeEmailData
  | BaseLenderEmailData
  | LenderPayoffReminderEmailData
  | LenderQcDocsDraftedArgs;
export type LenderSideEffectEmailHtmlData =
  | LenderPaymentEmailData
  | LenderPaymentFailedEmailData
  | LenderPayoffReminderEmailData
  | LenderSweepEmailData;
export type ExternalEmailHtmlData = MortgageeLetterEmailData;
export type EmailHtmlData =
  | BorrowerEmailHtmlData
  | LenderEmailHtmlData
  | DwollaAccountData
  | ExternalEmailHtmlData
  | NoEmailData;

interface NoEmailData {}

export interface BaseLenderEmailData {
  appUrl: string;
}

interface BaseLenderPaymentEmailData {
  appUrl?: string; // Some lender emails have button links
}

export interface DwollaAccountData extends BaseLenderPaymentEmailData {
  date: string;
  bankName: string;
  bankAccountIdentifier: string;
}
export interface BorrowerDwollaAccountData
  extends
    Omit<
      BorrowerEmailData,
      | 'losId'
      | 'maskedLosId'
      | 'propertyAddressLine1'
      | 'propertyAddressLine2'
      | 'propertyAddresses'
      | 'entityName'
      | 'allBorrowerNames'
    >,
    DwollaAccountData {}

export interface LenderWelcomeEmailData extends BaseLenderEmailData {
  passwordUrl: string;
}

export interface LenderPaymentEmailData extends BaseLenderPaymentEmailData {
  losId: LosId;
  maskedLosId: string;
  paymentDate: string;
  paymentMethod: string;
  paymentAmount: string;
  amountDueRemaining: string;
  bankAccountIdentifiers: string[];
  borrowerName: string;
}

export interface LenderPaymentFailedEmailData {
  losId: LosId;
  maskedLosId: string;
  paymentMethod: string;
  paymentAmount: string;
  borrowerName: string | undefined;
  paymentDate: string;
  appUrl?: string;
  achType: 'dwolla' | 'usio' | 'repay' | undefined; // in case we extend the option to make other payment types as failed in the future
  failureCode: string | undefined; // ACH Rxx code
  failureReason: string | undefined; // Failure reason if payment fails before initiating
}

export interface LenderPayoffReminderEmailData extends BaseLenderEmailData {
  losId: LosId;
  maturityDate: string;
}

export interface LenderSweepEmailData extends BaseLenderPaymentEmailData {
  paymentAmount: string;
  sourceBankAccountIdentifier: string;
  destinationBankAccountIdentifier: string;
}

export interface LenderQcDocsDraftedArgs extends BaseLenderEmailData {
  createdCount: number;
  qcCount: number;
  skippedCount: number;
  failedCount: number;
}

interface LenderDownloadEmailData {
  downloadUrl: string;
}

// Customizable template settings are used by Companies
// This is where we'd eventually customize the subject and pre-header
export const EmailTemplateSettings = z.object({
  partials: z.object({
    base: z.string().nonempty(),
    main: z.string().nonempty(),
    footer: z.string().nonempty(),
  }),
  subject: z.string().nonempty(),
  header: z.string().nonempty(),
  generated: z.string().nonempty(),
});
export type EmailTemplateSettings = z.infer<typeof EmailTemplateSettings>;

export type MjmlParseError = {
  line: number;
  message: string;
};
